<template>
  <div class="workspace-backlog-page">
    <div class="page-header">
      <div>
        <h1 class="page-title">{{ t('backlog.title') }}</h1>
        <p class="page-subtitle">{{ t('backlog.subtitle', { name: workspaceName }) }}</p>
      </div>
      <div class="header-actions">
        <input
          v-model="searchTerm"
          type="text"
          class="search-input"
          :placeholder="workspaceType === 'TAREFAS' ? t('backlog.searchTasks') : t('backlog.searchLeads')"
        />
        <button class="action-btn" @click="exportExcel">{{ t('backlog.exportExcel') }}</button>
        <button class="action-btn primary" @click="openPdfPreviewModal">{{ t('backlog.exportPdf') }}</button>
      </div>
    </div>

    <div v-if="loading" class="state-card">{{ t('backlog.loading') }}</div>
    <div v-else-if="groupedColumns.length === 0" class="state-card">{{ t('backlog.noColumns') }}</div>
    <div v-else class="groups-container">
      <section v-for="group in groupedColumns" :key="group.id" class="group-card">
        <header class="group-header">
          <div class="group-title-wrap">
            <span class="group-dot" :style="{ backgroundColor: group.cor || '#64748b' }"></span>
            <h3 class="group-title">{{ group.nome }}</h3>
          </div>
          <span class="group-count">{{ group.items.length }}</span>
        </header>

        <div v-if="group.items.length === 0" class="empty-group">{{ t('backlog.emptyColumn') }}</div>

        <div v-else class="rows-container">
          <article
            v-for="item in group.items"
            :key="itemKey(item)"
            class="backlog-row"
            @click="openItemDetail(item)"
          >
            <div class="row-color" :style="{ backgroundColor: item.cor || '#64748b' }"></div>

            <div class="row-main">
              <div class="row-top">
                <div class="row-title-wrap">
                  <h4 class="row-title">{{ getItemTitle(item) }}</h4>
                  <span class="row-type">{{ workspaceType === 'TAREFAS' ? t('backlog.typeTask') : t('backlog.typeLead') }}</span>
                </div>
                <div v-if="getTopTags(item).length > 0" class="row-top-tags">
                  <span v-for="tag in getTopTags(item)" :key="`top-tag-${tag.id}`" class="top-tag-chip" :style="getTagChipStyle(tag)">
                    <span class="top-tag-icon">{{ getTagIcon(tag) }}</span>
                    {{ tag.nome }}
                  </span>
                  <span v-if="getItemTags(item).length > MAX_TOP_TAGS" class="top-tag-chip muted">
                    +{{ getItemTags(item).length - MAX_TOP_TAGS }}
                  </span>
                </div>
              </div>
              <p class="row-description">{{ getItemDescription(item) }}</p>

              <div class="row-meta">
                <div class="progress-chip">
                  <div class="progress-chip-head">
                    <span class="progress-label">{{ workspaceType === 'TAREFAS' ? t('backlog.progress') : t('backlog.probability') }}</span>
                    <span class="progress-value">{{ getProgressPercent(item) }}%</span>
                  </div>
                  <div class="progress-bar-track">
                    <div class="progress-bar-fill" :style="{ width: `${getProgressPercent(item)}%` }"></div>
                  </div>
                </div>
                <template v-if="workspaceType === 'TAREFAS'">
                  <div class="meta-info-chip">
                    <div class="meta-info-head">
                      <span class="meta-info-label">{{ t('backlog.checklist') }}</span>
                      <span class="meta-info-value">{{ getChecklistValue(item) }}</span>
                    </div>
                  </div>
                  <div class="meta-info-chip">
                    <div class="meta-info-head">
                      <span class="meta-info-label">{{ t('backlog.financial') }}</span>
                      <MoneyAmount :value="getFinanceAmount(item)" size="sm" class="meta-info-value" />
                    </div>
                  </div>
                </template>
                <template v-else>
                  <div class="meta-info-chip">
                    <div class="meta-info-head">
                      <span class="meta-info-label">{{ t('backlog.name') }}</span>
                      <span class="meta-info-value">{{ getLeadName(item) }}</span>
                    </div>
                  </div>
                  <div class="meta-info-chip">
                    <div class="meta-info-head">
                      <span class="meta-info-label">{{ t('backlog.cnpj') }}</span>
                      <span class="meta-info-value">{{ getLeadCnpj(item) }}</span>
                    </div>
                  </div>
                  <div class="meta-info-chip">
                    <div class="meta-info-head">
                      <span class="meta-info-label">{{ t('backlog.contact') }}</span>
                      <span class="meta-info-value">{{ getLeadContact(item) }}</span>
                    </div>
                  </div>
                  <div class="meta-info-chip">
                    <div class="meta-info-head">
                      <span class="meta-info-label">{{ t('backlog.value') }}</span>
                      <MoneyAmount :value="item?.valorEstimado" size="sm" class="meta-info-value" />
                    </div>
                  </div>
                </template>
                <div class="assigned-users-wrap" :title="getAssignedUsersTitle(item)">
                  <div class="assigned-chip-head">
                    <span class="assigned-users-label">{{ t('backlog.assignedUsers') }}</span>
                    <span class="assigned-users-count">{{ getAssignedUsers(item).length }}</span>
                  </div>
                  <div class="assigned-users">
                    <template v-if="getAssignedUsers(item).length > 0">
                      <div v-for="user in getAssignedUsers(item).slice(0, 3)" :key="`assigned-${itemKey(item)}-${user.id}`" class="assigned-user">
                        <img
                          v-if="resolveAvatarUrl(user.fotoPerfil)"
                          :src="resolveAvatarUrl(user.fotoPerfil)"
                          :alt="userDisplayName(user)"
                          class="assigned-avatar"
                        />
                        <div v-else class="assigned-avatar initials">{{ getUserInitials(userDisplayName(user)) }}</div>
                        <span class="assigned-name">{{ userDisplayName(user) }}</span>
                      </div>
                      <span v-if="getAssignedUsers(item).length > 3" class="assigned-overflow">+{{ getAssignedUsers(item).length - 3 }}</span>
                    </template>
                    <span v-else class="assigned-empty">{{ t('backlog.noAssignment') }}</span>
                  </div>
                </div>
              </div>

              <div class="comments-wrap" @click.stop>
                <div class="comments-heading">
                  {{ t('backlog.comments', { count: getComments(item).length }) }}
                </div>

                <div class="comments-panel">
                  <div v-if="commentsLoading[itemKey(item)]" class="comments-state">{{ t('backlog.commentsLoading') }}</div>
                  <div v-else-if="getComments(item).length === 0" class="comments-state">{{ t('backlog.noComments') }}</div>

                  <div v-else class="comments-list">
                    <div v-for="comment in getComments(item)" :key="comment.id" class="comment-item">
                      <div class="comment-head">
                        <div class="comment-author">
                          <img
                            v-if="resolveAvatarUrl(comment.usuarioFotoPerfil)"
                            :src="resolveAvatarUrl(comment.usuarioFotoPerfil)"
                            :alt="commentUserLabel(comment.usuarioNome)"
                            class="comment-avatar"
                          />
                          <div v-else class="comment-avatar initials">{{ getUserInitials(commentUserLabel(comment.usuarioNome)) }}</div>
                          <span class="comment-user">{{ commentUserLabel(comment.usuarioNome) }}</span>
                        </div>
                        <div class="comment-head-right">
                          <span class="comment-time">{{ formatDateTime(comment.dataCriacao) }}</span>
                          <button class="comment-delete" :title="t('backlog.deleteComment')" :aria-label="t('backlog.deleteComment')" @click.stop="deleteComment(item, comment)">
                            <svg viewBox="0 0 20 20" fill="currentColor" class="comment-delete-icon" aria-hidden="true">
                              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                            </svg>
                          </button>
                        </div>
                      </div>
                      <p class="comment-text">{{ comment.texto }}</p>
                    </div>
                  </div>

                  <div class="comment-input-row">
                    <input
                      v-model="newCommentByKey[itemKey(item)]"
                      type="text"
                      class="comment-input"
                      :placeholder="t('backlog.commentPlaceholder')"
                      @keydown.enter.prevent="submitComment(item)"
                    />
                    <button class="comment-send" @click="submitComment(item)">{{ t('backlog.send') }}</button>
                  </div>
                </div>
              </div>
            </div>
          </article>
        </div>
      </section>
    </div>

    <TaskDetailModal
      v-if="showTaskDetailModal"
      :is-open="showTaskDetailModal"
      :task="selectedTask"
      @close="showTaskDetailModal = false"
      @save="handleTaskSave"
      @taskUpdated="handleTaskUpdated"
    />

    <LeadDetailModal
      v-if="showLeadDetailModal"
      :is-open="showLeadDetailModal"
      :lead="selectedLead"
      @close="showLeadDetailModal = false"
      @save="handleLeadSave"
      @lead-updated="handleLeadUpdated"
    />

    <div v-if="showPdfPreviewModal" class="pdf-preview-overlay" @click="showPdfPreviewModal = false">
      <div class="pdf-preview-modal" @click.stop>
        <header class="pdf-preview-header">
          <h3>{{ t('backlog.pdfPreviewTitle') }}</h3>
          <button class="pdf-close-btn" @click="showPdfPreviewModal = false">{{ t('backlog.close') }}</button>
        </header>

        <div ref="pdfPreviewContentRef" class="pdf-preview-content">
          <div ref="pdfRenderRef" class="pdf-render-area">
            <div class="pdf-render-header">
              <h3>{{ t('backlog.title') }}</h3>
              <p>{{ workspaceName }} - {{ workspaceType === 'TAREFAS' ? t('backlog.pdfHeaderTypeTasks') : t('backlog.pdfHeaderTypeLeads') }}</p>
            </div>
            <section v-for="group in groupedColumns" :key="`preview-${group.id}`" class="group-card pdf-group-card">
              <header class="group-header">
                <div class="group-title-wrap">
                  <span class="group-dot" :style="{ backgroundColor: group.cor || '#64748b' }"></span>
                  <h3 class="group-title">{{ group.nome }}</h3>
                </div>
                <span class="group-count">{{ group.items.length }}</span>
              </header>

              <div v-if="group.items.length === 0" class="empty-group">{{ t('backlog.emptyColumn') }}</div>

              <div v-else class="rows-container">
                <article v-for="item in group.items" :key="`preview-item-${itemKey(item)}`" class="backlog-row">
                  <div class="row-color" :style="{ backgroundColor: item.cor || '#64748b' }"></div>
                  <div class="row-main">
                    <div class="row-top">
                      <div class="row-title-wrap">
                        <h4 class="row-title">{{ getItemTitle(item) }}</h4>
                        <span class="row-type">{{ workspaceType === 'TAREFAS' ? t('backlog.typeTask') : t('backlog.typeLead') }}</span>
                      </div>
                      <div v-if="getTopTags(item).length > 0" class="row-top-tags">
                        <span v-for="tag in getTopTags(item)" :key="`preview-top-tag-${tag.id}`" class="top-tag-chip" :style="getTagChipStyle(tag)">
                          <span class="top-tag-icon">{{ getTagIcon(tag) }}</span>
                          {{ tag.nome }}
                        </span>
                        <span v-if="getItemTags(item).length > MAX_TOP_TAGS" class="top-tag-chip muted">
                          +{{ getItemTags(item).length - MAX_TOP_TAGS }}
                        </span>
                      </div>
                    </div>

                    <p class="row-description">{{ getItemDescription(item) }}</p>

                    <div class="row-meta">
                      <div class="progress-chip">
                        <div class="progress-chip-head">
                          <span class="progress-label">{{ workspaceType === 'TAREFAS' ? t('backlog.progress') : t('backlog.probability') }}</span>
                          <span class="progress-value">{{ getProgressPercent(item) }}%</span>
                        </div>
                        <div class="progress-bar-track">
                          <div class="progress-bar-fill" :style="{ width: `${getProgressPercent(item)}%` }"></div>
                        </div>
                      </div>
                      <template v-if="workspaceType === 'TAREFAS'">
                        <div class="meta-info-chip">
                          <div class="meta-info-head">
                            <span class="meta-info-label">{{ t('backlog.checklist') }}</span>
                            <span class="meta-info-value">{{ getChecklistValue(item) }}</span>
                          </div>
                        </div>
                        <div class="meta-info-chip">
                          <div class="meta-info-head">
                            <span class="meta-info-label">{{ t('backlog.financial') }}</span>
                            <MoneyAmount :value="getFinanceAmount(item)" size="sm" class="meta-info-value" />
                          </div>
                        </div>
                      </template>
                      <template v-else>
                        <div class="meta-info-chip">
                          <div class="meta-info-head">
                            <span class="meta-info-label">{{ t('backlog.name') }}</span>
                            <span class="meta-info-value">{{ getLeadName(item) }}</span>
                          </div>
                        </div>
                        <div class="meta-info-chip">
                          <div class="meta-info-head">
                            <span class="meta-info-label">{{ t('backlog.cnpj') }}</span>
                            <span class="meta-info-value">{{ getLeadCnpj(item) }}</span>
                          </div>
                        </div>
                        <div class="meta-info-chip">
                          <div class="meta-info-head">
                            <span class="meta-info-label">{{ t('backlog.contact') }}</span>
                            <span class="meta-info-value">{{ getLeadContact(item) }}</span>
                          </div>
                        </div>
                        <div class="meta-info-chip">
                          <div class="meta-info-head">
                            <span class="meta-info-label">{{ t('backlog.value') }}</span>
                            <MoneyAmount :value="item?.valorEstimado" size="sm" class="meta-info-value" />
                          </div>
                        </div>
                      </template>
                      <div class="assigned-users-wrap" :title="getAssignedUsersTitle(item)">
                        <div class="assigned-chip-head">
                          <span class="assigned-users-label">{{ t('backlog.assignedUsers') }}</span>
                          <span class="assigned-users-count">{{ getAssignedUsers(item).length }}</span>
                        </div>
                        <div class="assigned-users">
                          <template v-if="getAssignedUsers(item).length > 0">
                            <div v-for="user in getAssignedUsers(item).slice(0, 3)" :key="`preview-assigned-${itemKey(item)}-${user.id}`" class="assigned-user">
                              <img
                                v-if="resolveAvatarUrl(user.fotoPerfil)"
                                :src="resolveAvatarUrl(user.fotoPerfil)"
                                :alt="userDisplayName(user)"
                                class="assigned-avatar"
                              />
                              <div v-else class="assigned-avatar initials">{{ getUserInitials(userDisplayName(user)) }}</div>
                              <span class="assigned-name">{{ userDisplayName(user) }}</span>
                            </div>
                            <span v-if="getAssignedUsers(item).length > 3" class="assigned-overflow">+{{ getAssignedUsers(item).length - 3 }}</span>
                          </template>
                          <span v-else class="assigned-empty">{{ t('backlog.noAssignment') }}</span>
                        </div>
                      </div>
                    </div>

                    <div class="comments-wrap">
                      <div class="comments-heading">{{ t('backlog.comments', { count: getComments(item).length }) }}</div>
                      <div class="comments-panel">
                        <div v-if="getComments(item).length === 0" class="comments-state">{{ t('backlog.noComments') }}</div>
                        <div v-else class="comments-list">
                          <div v-for="comment in getComments(item)" :key="`preview-comment-${comment.id}`" class="comment-item">
                            <div class="comment-head">
                              <div class="comment-author">
                                <img
                                  v-if="resolveAvatarUrl(comment.usuarioFotoPerfil)"
                                  :src="resolveAvatarUrl(comment.usuarioFotoPerfil)"
                                  :alt="commentUserLabel(comment.usuarioNome)"
                                  class="comment-avatar"
                                />
                                <div v-else class="comment-avatar initials">{{ getUserInitials(commentUserLabel(comment.usuarioNome)) }}</div>
                                <span class="comment-user">{{ commentUserLabel(comment.usuarioNome) }}</span>
                              </div>
                              <div class="comment-head-right">
                                <span class="comment-time">{{ formatDateTime(comment.dataCriacao) }}</span>
                              </div>
                            </div>
                            <p class="comment-text">{{ comment.texto }}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </article>
              </div>
            </section>
          </div>
        </div>

        <footer class="pdf-preview-footer">
          <button class="action-btn" @click="showPdfPreviewModal = false">{{ t('backlog.cancel') }}</button>
          <button class="action-btn primary" @click="confirmExportPdf">{{ t('backlog.exportPdf') }}</button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { useWorkspaceStore } from '@/stores/workspace'
import TaskDetailModal from '@/components/TaskDetailModal.vue'
import LeadDetailModal from '@/components/LeadDetailModal.vue'
import api from '@/services/api'
import { buildKanbanWebSocketUrl } from '@/config/app'
import { getTagIcon as getIconFromMapper } from '@/utils/iconMapper'
import * as XLSX from 'xlsx'
import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'
import MoneyAmount from '@/components/MoneyAmount.vue'
import { formatMoneyAmount } from '@/utils/format'

const { t, locale } = useI18n()
const route = useRoute()
const workspaceStore = useWorkspaceStore()
const workspaceId = ref(Number(route.params.id))

const loading = ref(true)
const searchTerm = ref('')
const columns = ref([])
const tasks = ref([])
const leads = ref([])
const workspaceTags = ref([])
const workspaceUsers = ref([])
const showPdfPreviewModal = ref(false)
const pdfRenderRef = ref(null)
const pdfPreviewContentRef = ref(null)
const MAX_TOP_TAGS = 3

const commentsByKey = ref({})
const commentsLoading = ref({})
const commentsFetched = ref({})
const newCommentByKey = ref({})

const showTaskDetailModal = ref(false)
const showLeadDetailModal = ref(false)
const selectedTask = ref(null)
const selectedLead = ref(null)

let ws = null
let reconnectTimer = null
let wsIntentionalClose = false
let wsConnectGeneration = 0
let reloadTimer = null

const workspace = computed(() => workspaceStore.currentWorkspace)
const workspaceName = computed(() => workspace.value?.nome || t('workspaceDashboard.defaultName'))
const workspaceType = computed(() => workspace.value?.tipo || 'TAREFAS')

const allItems = computed(() => {
  const base = workspaceType.value === 'TAREFAS' ? tasks.value : leads.value
  const query = searchTerm.value.trim().toLowerCase()
  if (!query) return base
  return base.filter(item => {
    const title = getItemTitle(item).toLowerCase()
    const desc = getItemDescription(item).toLowerCase()
    return title.includes(query) || desc.includes(query)
  })
})

const groupedColumns = computed(() => {
  return (columns.value || []).map(col => {
    const items = allItems.value
      .filter(item => Number(item.colunaId) === Number(col.id) && item.isAtivo !== false)
      .sort((a, b) => Number(a.posicao || 0) - Number(b.posicao || 0))
    return { ...col, items }
  })
})

const itemKey = (item) => {
  const prefix = workspaceType.value === 'TAREFAS' ? 'task' : 'lead'
  return `${prefix}-${item.id}`
}

const getItemTitle = (item) => workspaceType.value === 'TAREFAS'
  ? (item.titulo || t('backlog.taskFallback', { id: item.id }))
  : (item.nome || t('backlog.leadFallback', { id: item.id }))
const getItemDescription = (item) => {
  if (workspaceType.value === 'TAREFAS') return item.descricao || t('backlog.noDescription')
  return item.descricao || item.empresa || t('backlog.noDescription')
}

const parseJsonArray = (value) => {
  if (!value) return []
  if (Array.isArray(value)) return value
  try {
    const parsed = JSON.parse(value)
    return Array.isArray(parsed) ? parsed : []
  } catch (e) {
    return []
  }
}

const getItemTags = (item) => {
  const tagIds = parseJsonArray(item.tags)
  if (!tagIds.length) return []
  const allTags = workspaceTags.value.flatMap(cat => Array.isArray(cat.tags) ? cat.tags : [])
  return allTags.filter(t => tagIds.includes(t.id) || tagIds.includes(String(t.id)))
}
const getTopTags = (item) => getItemTags(item).slice(0, MAX_TOP_TAGS)

const normalizeHex = (hexColor) => {
  if (!hexColor) return '#6366f1'
  const clean = String(hexColor).trim()
  if (!clean) return '#6366f1'
  return clean.startsWith('#') ? clean : `#${clean}`
}

const getTagTextColor = (hexColor) => {
  const hex = normalizeHex(hexColor).replace('#', '')
  if (hex.length !== 6) return '#ffffff'
  const r = parseInt(hex.slice(0, 2), 16)
  const g = parseInt(hex.slice(2, 4), 16)
  const b = parseInt(hex.slice(4, 6), 16)
  if ([r, g, b].some(Number.isNaN)) return '#ffffff'
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminance > 0.6 ? '#111827' : '#ffffff'
}

const getTagChipStyle = (tag) => {
  const backgroundColor = normalizeHex(tag?.cor || '#6366f1')
  return {
    backgroundColor,
    color: getTagTextColor(backgroundColor),
    borderColor: 'transparent'
  }
}

const getTagIcon = (tag) => {
  if (!tag?.icone) return '🏷️'
  return getIconFromMapper(tag.icone) || tag.icone || '🏷️'
}

const getChecklistProgress = (item) => {
  if (workspaceType.value !== 'TAREFAS') return t('backlog.checklistNa')
  const checklist = parseJsonArray(item.checklist)
  let total = 0
  let done = 0
  checklist.forEach(cat => {
    const items = Array.isArray(cat.items) ? cat.items : []
    items.forEach(ci => {
      total += 1
      if (ci.concluido === true) done += 1
    })
  })
  if (total === 0) return t('backlog.checklistCount', { done: 0, total: 0 })
  return t('backlog.checklistCount', { done, total })
}

const getFinanceAmount = (item) => {
  if (workspaceType.value === 'TAREFAS') {
    const recebiveis = Array.isArray(item?.recebiveis)
      ? item.recebiveis
      : (typeof item?.recebiveis === 'string' ? parseJsonArray(item.recebiveis) : [])
    return (Array.isArray(recebiveis) ? recebiveis : []).reduce((acc, atual) => {
      if (atual?.pago) return acc
      return acc + Number(atual?.valor || 0)
    }, 0)
  }
  return Number(item?.valorEstimado || 0)
}

const getFinanceLabel = (item) => {
  const amount = formatMoneyAmount(getFinanceAmount(item), locale.value)
  return t('backlog.financialLabel', { amount })
}

const getChecklistValue = (item) => {
  const raw = getChecklistProgress(item)
  return String(raw).replace(/^Checklist:\s*/i, '')
}

const getLeadName = (item) => {
  const value = item?.nome || item?.nomeFantasia || item?.razaoSocial || ''
  return String(value || '-')
}

const getLeadCnpj = (item) => {
  const value = item?.cnpj || item?.cnpjEmpresa || ''
  return String(value || '-')
}

const getLeadContact = (item) => {
  const value = item?.telefone || item?.celular || item?.email || ''
  return String(value || '-')
}

const getProgressPercent = (item) => {
  const raw = workspaceType.value === 'TAREFAS'
    ? Number(item.progressoTarefaPercentual || 0)
    : Number(item.probabilidade || 0)
  if (!Number.isFinite(raw)) return 0
  return Math.max(0, Math.min(100, Math.round(raw)))
}

const getAssignedUserIds = (item) => {
  if (workspaceType.value === 'TAREFAS') return parseJsonArray(item.assignedUsers)
  return parseJsonArray(item.usuariosAtribuidos)
}

const getAssignedUsers = (item) => {
  const ids = getAssignedUserIds(item)
  if (!ids.length) return []
  const normalizedIds = ids.map(id => String(id))
  return workspaceUsers.value
    .filter(user => normalizedIds.includes(String(user.id)))
    .map(user => ({
      id: user.id,
      nomeCompleto: user.nomeCompleto || user.nome || t('backlog.user', { id: user.id }),
      fotoPerfil: user.fotoPerfil || null
    }))
}

const getAssignedUsersTitle = (item) => {
  const users = getAssignedUsers(item)
  if (!users.length) return t('backlog.noAssignment')
  return users.map(u => u.nomeCompleto).join(', ')
}

const resolveAvatarUrl = (url) => {
  if (!url) return null
  const clean = String(url).trim()
  if (!clean) return null
  if (/^https?:\/\//i.test(clean) || clean.startsWith('data:')) return clean
  if (clean.startsWith('/')) {
    const base = (api.defaults.baseURL || '').replace(/\/+$/, '')
    return `${base}${clean}`
  }
  return clean
}

const userDisplayName = (user) =>
  user?.nomeCompleto || user?.nome || t('backlog.user', { id: user?.id })
const commentUserLabel = (name) => name || t('common.user')

const getUserInitials = (name) => {
  const full = String(name || '').trim()
  if (!full) return 'U'
  const parts = full.split(/\s+/).filter(Boolean)
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return `${parts[0][0] || ''}${parts[parts.length - 1][0] || ''}`.toUpperCase()
}

const formatDateTime = (value) => {
  if (!value) return ''
  if (Array.isArray(value)) {
    const [y, m, d, h = 0, min = 0, s = 0] = value
    return `${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}/${y} ${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`
  }
  const raw = String(value).trim().replace(' ', 'T')
  const date = new Date(raw)
  if (!Number.isNaN(date.getTime())) return date.toLocaleString(locale.value)
  return String(value)
}

const loadWorkspace = async () => {
  await workspaceStore.loadWorkspace(workspaceId.value)
}

const loadColumns = async () => {
  const response = await api.get(`/colunas-kanban/workspace/${workspaceId.value}`)
  columns.value = Array.isArray(response.data) ? response.data : []
}

const loadItems = async () => {
  if (workspaceType.value === 'TAREFAS') {
    const response = await api.get(`/tasks/workspace/${workspaceId.value}`, { params: { _t: Date.now() } })
    tasks.value = Array.isArray(response.data) ? response.data : []
    leads.value = []
  } else {
    const response = await api.get(`/leads/workspace/${workspaceId.value}`, { params: { _t: Date.now() } })
    leads.value = Array.isArray(response.data) ? response.data : []
    tasks.value = []
  }
}

const loadWorkspaceTags = async () => {
  try {
    const response = await api.get(`/workspace-config/${workspaceId.value}/configuracoes`)
    workspaceTags.value = response.data?.categorias || []
  } catch (e) {
    workspaceTags.value = []
  }
}

const loadWorkspaceUsers = async () => {
  try {
    const response = await api.get(`/workspace-acesso/workspace/${workspaceId.value}`)
    const usersRaw = Array.isArray(response.data) ? response.data : []
    workspaceUsers.value = usersRaw.map(usuario => ({
      id: usuario.usuarioId ?? usuario.id,
      nomeCompleto: usuario.usuarioNome ?? usuario.nomeCompleto ?? usuario.nome,
      fotoPerfil: usuario.usuarioFotoPerfil ?? usuario.fotoPerfil ?? null,
      roleWorkspace: usuario.roleWorkspace
    }))
  } catch (e) {
    workspaceUsers.value = []
  }
}

const reloadAll = async () => {
  await Promise.all([loadColumns(), loadItems()])
  await preloadComments()
}

const scheduleReload = () => {
  if (reloadTimer) clearTimeout(reloadTimer)
  reloadTimer = setTimeout(async () => {
    await reloadAll()
  }, 250)
}

const openItemDetail = (item) => {
  if (workspaceType.value === 'TAREFAS') {
    selectedTask.value = item
    showTaskDetailModal.value = true
  } else {
    selectedLead.value = item
    showLeadDetailModal.value = true
  }
}

const handleTaskSave = async (taskData) => {
  if (!selectedTask.value?.id) return
  const response = await api.put(`/tasks/${selectedTask.value.id}`, taskData)
  const idx = tasks.value.findIndex(t => Number(t.id) === Number(selectedTask.value.id))
  if (idx !== -1) tasks.value[idx] = response.data
  showTaskDetailModal.value = false
}

const handleLeadSave = async (leadData) => {
  if (!selectedLead.value?.id) return
  const response = await api.put(`/leads/${selectedLead.value.id}`, leadData)
  const idx = leads.value.findIndex(l => Number(l.id) === Number(selectedLead.value.id))
  if (idx !== -1) leads.value[idx] = response.data
  showLeadDetailModal.value = false
}

const handleTaskUpdated = (updatedTask) => {
  const idx = tasks.value.findIndex(t => Number(t.id) === Number(updatedTask.id))
  if (idx !== -1) tasks.value[idx] = { ...tasks.value[idx], ...updatedTask }
  if (selectedTask.value && Number(selectedTask.value.id) === Number(updatedTask.id)) {
    selectedTask.value = { ...selectedTask.value, ...updatedTask }
  }
}

const handleLeadUpdated = (updatedLead) => {
  const idx = leads.value.findIndex(l => Number(l.id) === Number(updatedLead.id))
  if (idx !== -1) leads.value[idx] = { ...leads.value[idx], ...updatedLead }
  if (selectedLead.value && Number(selectedLead.value.id) === Number(updatedLead.id)) {
    selectedLead.value = { ...selectedLead.value, ...updatedLead }
  }
}

const getComments = (item) => commentsByKey.value[itemKey(item)] || []

const loadComments = async (item, force = false) => {
  const key = itemKey(item)
  if (!force && commentsFetched.value[key]) return
  commentsLoading.value[key] = true
  try {
    const endpoint = workspaceType.value === 'TAREFAS'
      ? `/backlog-comments/task/${item.id}`
      : `/backlog-comments/lead/${item.id}`
    const response = await api.get(endpoint, { params: { _t: Date.now() } })
    commentsByKey.value[key] = Array.isArray(response.data) ? response.data : []
    commentsFetched.value[key] = true
  } catch (e) {
    commentsByKey.value[key] = []
  } finally {
    commentsLoading.value[key] = false
  }
}

const preloadComments = async () => {
  const items = groupedColumns.value.flatMap(group => group.items || [])
  if (!items.length) return
  await Promise.all(items.map(item => loadComments(item)))
}

const submitComment = async (item) => {
  const key = itemKey(item)
  const texto = (newCommentByKey.value[key] || '').trim()
  if (!texto) return
  try {
    const payload = workspaceType.value === 'TAREFAS'
      ? { taskId: item.id, texto }
      : { leadId: item.id, texto }
    await api.post('/backlog-comments', payload)
    newCommentByKey.value[key] = ''
    await loadComments(item, true)
  } catch (e) {
    console.error('Erro ao comentar no backlog:', e)
  }
}

const deleteComment = async (item, comment) => {
  if (!comment?.id) return
  try {
    await api.delete(`/backlog-comments/${comment.id}`)
    await loadComments(item, true)
  } catch (e) {
    console.error('Erro ao excluir comentario no backlog:', e)
  }
}

const collectExportRows = () => {
  const rows = []
  groupedColumns.value.forEach(group => {
    group.items.forEach(item => {
      const tags = getItemTags(item).map(t => t.nome).join(', ')
      if (workspaceType.value === 'TAREFAS') {
        rows.push({
          Coluna: group.nome,
          Tipo: 'Task',
          Nome: getItemTitle(item),
          Descricao: getItemDescription(item),
          Tags: tags || '-',
          ChecklistOuProgresso: getChecklistProgress(item),
          Financeiro: getFinanceLabel(item),
          Progresso: `${getProgressPercent(item)}%`,
          Atribuidos: getAssignedUsersTitle(item)
        })
      } else {
        rows.push({
          Coluna: group.nome,
          Tipo: 'Lead',
          Nome: getLeadName(item),
          CNPJ: getLeadCnpj(item),
          Contato: getLeadContact(item),
          Valor: formatMoneyAmount(item?.valorEstimado, locale.value),
          Probabilidade: `${getProgressPercent(item)}%`,
          Tags: tags || '-',
          Atribuidos: getAssignedUsersTitle(item)
        })
      }
    })
  })
  return rows
}

const exportExcel = () => {
  const rows = collectExportRows()
  const sheet = XLSX.utils.json_to_sheet(rows)
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, sheet, t('backlog.excelSheet'))
  XLSX.writeFile(wb, `backlog-workspace-${workspaceId.value}.xlsx`)
}

const exportPdfFromPreview = async () => {
  const target = pdfPreviewContentRef.value || pdfRenderRef.value
  if (!target) return

  const previousMaxHeight = target.style.maxHeight
  const previousOverflow = target.style.overflow
  const previousHeight = target.style.height

  target.style.maxHeight = 'none'
  target.style.overflow = 'visible'
  target.style.height = 'auto'

  const canvas = await html2canvas(target, {
    scale: 2,
    useCORS: true,
    backgroundColor: '#ffffff',
    windowWidth: target.scrollWidth,
    windowHeight: target.scrollHeight
  })

  target.style.maxHeight = previousMaxHeight
  target.style.overflow = previousOverflow
  target.style.height = previousHeight

  const imgData = canvas.toDataURL('image/png')
  const pdf = new jsPDF('p', 'mm', 'a4')
  const pageWidth = pdf.internal.pageSize.getWidth()
  const pageHeight = pdf.internal.pageSize.getHeight()

  const imgWidth = pageWidth
  const imgHeight = (canvas.height * imgWidth) / canvas.width
  let heightLeft = imgHeight
  let position = 0

  pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight)
  heightLeft -= pageHeight

  while (heightLeft > 0) {
    position = heightLeft - imgHeight
    pdf.addPage()
    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight)
    heightLeft -= pageHeight
  }

  pdf.save(`backlog-workspace-${workspaceId.value}.pdf`)
}

const openPdfPreviewModal = () => {
  showPdfPreviewModal.value = true
}

const confirmExportPdf = async () => {
  await nextTick()
  await exportPdfFromPreview()
  showPdfPreviewModal.value = false
}

const disposeWebSocket = () => {
  if (!ws) return
  const old = ws
  ws = null
  old.onopen = null
  old.onclose = null
  old.onerror = null
  old.onmessage = null
  try {
    old.close(1000, 'replaced')
  } catch (_) {
    /* ignore */
  }
}

const connectWebSocket = () => {
  const token = localStorage.getItem('token')
  if (!token || !workspaceId.value) return
  try {
    disposeWebSocket()
    if (reconnectTimer) {
      clearTimeout(reconnectTimer)
      reconnectTimer = null
    }

    const generation = ++wsConnectGeneration
    const wsUrl = buildKanbanWebSocketUrl(workspaceId.value, token)
    if (!wsUrl) return

    wsIntentionalClose = false
    const socket = new WebSocket(wsUrl)
    ws = socket

    socket.onmessage = async (evt) => {
      if (generation !== wsConnectGeneration || ws !== socket) return
      if (evt.data === 'pong') return
      try {
        const data = JSON.parse(evt.data || '{}')
        const type = data.type
        const payload = data.payload

        const commonEvents = ['column.created', 'column.updated', 'column.deleted', 'column.reordered']
        const taskEvents = ['task.updated', 'task.created', 'task.deleted', 'task.moved', 'task.reordered', 'task.status', 'task.archived']
        const leadEvents = ['lead.updated', 'lead.created', 'lead.deleted', 'lead.moved', 'lead.reordered', 'lead.status', 'lead.archived']

        if (commonEvents.includes(type)) {
          scheduleReload()
          return
        }
        if (workspaceType.value === 'TAREFAS' && taskEvents.includes(type)) {
          scheduleReload()
          return
        }
        if (workspaceType.value === 'CRM' && leadEvents.includes(type)) {
          scheduleReload()
          return
        }
        if (type === 'backlog.comment.created' && payload) {
          const isTaskPayload = payload.taskId != null
          if ((workspaceType.value === 'TAREFAS' && isTaskPayload) || (workspaceType.value === 'CRM' && !isTaskPayload)) {
            await loadComments({ id: isTaskPayload ? payload.taskId : payload.leadId }, true)
          }
        }
        if (type === 'backlog.comment.deleted' && payload) {
          const isTaskPayload = payload.taskId != null
          if ((workspaceType.value === 'TAREFAS' && isTaskPayload) || (workspaceType.value === 'CRM' && !isTaskPayload)) {
            await loadComments({ id: isTaskPayload ? payload.taskId : payload.leadId }, true)
          }
        }
      } catch (e) {
        console.error('[Backlog WS] erro parse:', e)
      }
    }

    socket.onclose = () => {
      if (generation !== wsConnectGeneration) return
      if (ws === socket) ws = null
      if (!wsIntentionalClose) {
        if (reconnectTimer) clearTimeout(reconnectTimer)
        reconnectTimer = setTimeout(connectWebSocket, 1500)
      }
    }
  } catch (e) {
    console.error('[Backlog WS] erro conexão:', e)
  }
}

const closeWebSocket = () => {
  wsIntentionalClose = true
  wsConnectGeneration++
  if (reconnectTimer) {
    clearTimeout(reconnectTimer)
    reconnectTimer = null
  }
  disposeWebSocket()
}

const initialize = async () => {
  loading.value = true
  try {
    await loadWorkspace()
    await Promise.all([loadColumns(), loadItems(), loadWorkspaceTags(), loadWorkspaceUsers()])
    await preloadComments()
  } finally {
    loading.value = false
  }
}

watch(() => route.params.id, async (id) => {
  workspaceId.value = Number(id)
  commentsByKey.value = {}
  commentsFetched.value = {}
  newCommentByKey.value = {}
  closeWebSocket()
  await initialize()
  wsIntentionalClose = false
  connectWebSocket()
})

onMounted(async () => {
  await initialize()
  connectWebSocket()
})

onBeforeUnmount(() => {
  closeWebSocket()
  if (reloadTimer) clearTimeout(reloadTimer)
})
</script>

<style scoped>
.workspace-backlog-page {
  padding: 20px 24px;
  min-height: calc(100vh - 64px);
}

.page-header {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}

.page-title {
  font-size: 30px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 6px;
}

.page-subtitle {
  font-size: 14px;
  color: var(--text-secondary);
  margin: 0;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.search-input {
  width: 260px;
  height: 36px;
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  background: var(--bg-secondary);
  color: var(--text-primary);
  padding: 0 10px;
}

.action-btn {
  height: 36px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid var(--border-primary);
  background: var(--bg-secondary);
  color: var(--text-primary);
  font-size: 13px;
  font-weight: 600;
}

.action-btn.primary {
  background: #2563eb;
  border-color: #2563eb;
  color: #fff;
}

.state-card {
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  padding: 20px;
  background: var(--bg-secondary);
  color: var(--text-secondary);
}

.groups-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.group-card {
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  background: var(--bg-secondary);
}

.group-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  border-bottom: 1px solid var(--border-primary);
}

.group-title-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
}

.group-dot {
  width: 10px;
  height: 10px;
  border-radius: 999px;
}

.group-title {
  margin: 0;
  font-size: 14px;
  color: var(--text-primary);
}

.group-count {
  font-size: 12px;
  color: var(--text-secondary);
}

.empty-group {
  padding: 12px;
  color: var(--text-secondary);
  font-size: 13px;
}

.rows-container {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px;
}

.backlog-row {
  display: flex;
  gap: 12px;
  padding: 12px 14px;
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  background: var(--bg-primary);
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.05);
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}

.backlog-row:hover {
  border-color: rgba(37, 99, 235, 0.35);
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.08);
  transform: translateY(-1px);
}

.row-color {
  width: 5px;
  border-radius: 999px;
  align-self: stretch;
}

.row-main {
  flex: 1;
  min-width: 0;
}

.row-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.row-title-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1;
}

.row-title {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  line-height: 1.35;
  word-break: break-word;
}

.row-type {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: #1d4ed8;
  background: rgba(37, 99, 235, 0.12);
  border: 1px solid rgba(37, 99, 235, 0.18);
  border-radius: 999px;
  padding: 2px 8px;
  white-space: nowrap;
}

.row-top-tags {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.top-tag-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 10px;
  line-height: 1;
  border-radius: 999px;
  border: 1px solid transparent;
  padding: 4px 8px;
  font-weight: 600;
}

.top-tag-icon {
  font-size: 12px;
  line-height: 1;
}

.top-tag-chip.muted {
  border-color: var(--border-primary);
  background: var(--bg-secondary);
  color: var(--text-secondary);
}

.row-description {
  margin: 6px 0 10px;
  color: var(--text-secondary);
  font-size: 12px;
  line-height: 1.45;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.row-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: stretch;
}

.meta-info-chip {
  min-width: 120px;
  min-height: 39px;
  border: 1px solid var(--border-primary);
  border-radius: 6px;
  padding: 6px 8px;
  background: var(--bg-secondary);
  display: flex;
}

.meta-info-head {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 2px;
  width: 100%;
}

.meta-info-label {
  font-size: 10px;
  color: var(--text-secondary);
  font-weight: 600;
}

.meta-info-value {
  font-size: 10px;
  color: var(--text-primary);
  font-weight: 700;
  margin-top: auto;
  align-self: flex-end;
  text-align: right;
}

.progress-chip {
  min-width: 120px;
  border: 1px solid var(--border-primary);
  border-radius: 6px;
  padding: 6px 8px;
  background: var(--bg-secondary);
}

.progress-chip-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
}

.progress-label {
  font-size: 10px;
  color: var(--text-secondary);
  font-weight: 600;
}

.progress-value {
  font-size: 10px;
  color: var(--text-primary);
  font-weight: 700;
}

.progress-bar-track {
  width: 100%;
  height: 6px;
  border-radius: 999px;
  background: rgba(148, 163, 184, 0.3);
  overflow: hidden;
}

.progress-bar-fill {
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, #3b82f6 0%, #2563eb 100%);
}

.assigned-users {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}

.assigned-users-wrap {
  min-width: 148px;
  border: 1px solid var(--border-primary);
  border-radius: 6px;
  padding: 4px 6px;
  background: var(--bg-secondary);
  display: inline-flex;
  flex-direction: column;
  align-items: stretch;
  gap: 4px;
  opacity: 0.92;
}

.assigned-chip-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
}

.assigned-users-label {
  font-size: 9px;
  color: var(--text-secondary);
  font-weight: 600;
  letter-spacing: 0.01em;
}

.assigned-users-count {
  font-size: 9px;
  color: var(--text-primary);
  font-weight: 700;
}

.assigned-user {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: 1px solid var(--border-primary);
  border-radius: 999px;
  background: color-mix(in srgb, var(--bg-secondary) 86%, transparent);
  padding: 1px 6px 1px 2px;
}

.assigned-avatar {
  width: 16px;
  height: 16px;
  border-radius: 999px;
  object-fit: cover;
  border: 1px solid var(--border-primary);
}

.assigned-avatar.initials {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 8px;
  font-weight: 700;
  color: #fff;
  background: #6366f1;
}

.assigned-name {
  font-size: 10px;
  color: var(--text-primary);
  max-width: 104px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.assigned-overflow {
  font-size: 10px;
  color: var(--text-secondary);
  font-weight: 600;
}

.assigned-empty {
  font-size: 10px;
  color: var(--text-secondary);
}

.comments-wrap {
  margin-top: 8px;
}

.comments-heading {
  color: var(--text-secondary);
  font-size: 11px;
  font-weight: 600;
  margin-bottom: 6px;
}

.comments-panel {
  border: 1px solid var(--border-primary);
  border-radius: 6px;
  padding: 8px;
  background: var(--bg-primary);
}

.comments-state {
  color: var(--text-secondary);
  font-size: 12px;
}

.comments-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 8px;
}

.comment-item {
  border: 1px solid var(--border-primary);
  border-radius: 5px;
  padding: 6px 8px;
  background: var(--bg-primary);
}

.comment-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
}

.comment-author {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.comment-avatar {
  width: 20px;
  height: 20px;
  border-radius: 999px;
  object-fit: cover;
  border: 1px solid var(--border-primary);
}

.comment-avatar.initials {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 700;
  color: #fff;
  background: #6366f1;
}

.comment-user {
  font-size: 11px;
  color: var(--text-primary);
  font-weight: 600;
}

.comment-head-right {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.comment-time {
  font-size: 10px;
  color: var(--text-secondary);
}

.comment-delete {
  width: 18px;
  height: 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: #dc2626;
  cursor: pointer;
  padding: 0;
  border-radius: 4px;
}

.comment-delete-icon {
  width: 14px;
  height: 14px;
}

.comment-delete:hover {
  background: rgba(220, 38, 38, 0.08);
}

.comment-text {
  margin: 4px 0 0;
  font-size: 12px;
  color: var(--text-primary);
}

.comment-input-row {
  display: flex;
  gap: 6px;
}

.comment-input {
  flex: 1;
  height: 30px;
  border: 1px solid var(--border-primary);
  border-radius: 6px;
  background: var(--bg-secondary);
  color: var(--text-primary);
  padding: 0 8px;
  font-size: 12px;
}

.comment-send {
  height: 30px;
  border: 1px solid #2563eb;
  background: #2563eb;
  color: #fff;
  border-radius: 6px;
  padding: 0 10px;
  font-size: 12px;
}

.pdf-preview-overlay {
  position: fixed;
  inset: 0;
  z-index: 70;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.pdf-preview-modal {
  width: min(1100px, 96vw);
  max-height: 90vh;
  overflow: hidden;
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: 14px;
  display: flex;
  flex-direction: column;
}

.pdf-preview-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid var(--border-primary);
}

.pdf-preview-header h3 {
  margin: 0;
  font-size: 15px;
  color: var(--text-primary);
}

.pdf-close-btn {
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  background: var(--bg-secondary);
  color: var(--text-secondary);
  font-size: 12px;
  padding: 6px 10px;
  cursor: pointer;
}

.pdf-preview-content {
  padding: 14px 16px;
  overflow: auto;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.pdf-render-area {
  background: var(--bg-primary);
  color: var(--text-primary);
  padding: 12px;
  border: 1px solid var(--border-primary);
  border-radius: 10px;
}

.pdf-render-header {
  margin-bottom: 10px;
}

.pdf-render-header h3 {
  margin: 0;
  font-size: 18px;
  color: var(--text-primary);
}

.pdf-render-header p {
  margin: 4px 0 0;
  font-size: 12px;
  color: var(--text-secondary);
}

.pdf-group-card {
  margin-bottom: 10px;
}

.pdf-preview-footer {
  border-top: 1px solid var(--border-primary);
  padding: 10px 16px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
</style>
